import type { View } from '../router/router';
import { authService } from '../services/auth.service';
import type { UserProfile } from '../services/auth.service';
import { apiClient } from '../services/api-client';
import {
  fetchAdminDashboardMetrics,
  type AdminDashboardMetrics,
} from '../services/admin-metrics.service';
import { formatDate } from '../utils/admin-labels';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface AttendanceStats {
  weeklyCount: number;
  monthlyCount: number;
  allTimeCount: number;
}

export class DashboardPage implements View {
  private user: UserProfile | null = null;
  private stats: AttendanceStats | null = null;
  private extraData: any = null;
  private adminMetrics: AdminDashboardMetrics | null = null;

  async render(): Promise<string> {
    this.user = authService.getUser();
    if (!this.user) return '<div class="main-content"><p>Cargando sesión...</p></div>';

    const welcomeMsg = this.getWelcomeMessage();
    const roleBadgeClass = `badge-${this.user.role.toLowerCase()}`;

    // Intentar consultar datos adicionales basados en el Rol
    await this.fetchRoleSpecificData();

    return `
      <div class="dashboard-header" style="margin-bottom: 1.5rem;">
        <div class="dashboard-title">
          <h2 style="font-size: 1.6rem; font-weight: 800; line-height: 1.2; margin-bottom: 0.25rem;">${welcomeMsg}, ${this.user.firstName}!</h2>
          <p style="color: var(--text-muted); font-size: 0.9rem;">Esta es tu central de entrenamiento y disciplina para el día de hoy.</p>
        </div>
        <div style="margin-top: 0.5rem;">
          <span class="badge ${roleBadgeClass}" style="font-size: 0.85rem; padding: 0.35rem 0.85rem;">
            Rol: ${this.user.role}
          </span>
        </div>
      </div>

      <!-- Métricas Principales del Dashboard -->
      ${
        this.user.role === 'STUDENT'
          ? this.renderStudentDashboard()
          : this.user.role === 'ADMIN'
            ? this.renderAdminDashboard()
            : this.renderStaffDashboard()
      }
    `;
  }

  afterRender(): void {}

  private getWelcomeMessage(): string {
    const hours = new Date().getHours();
    if (hours < 12) return 'Buenos días';
    if (hours < 20) return 'Buenas tardes';
    return 'Buenas noches';
  }

  private async fetchRoleSpecificData() {
    if (!this.user) return;

    try {
      if (this.user.role === 'STUDENT') {
        // Consultar estadísticas de asistencia del estudiante
        this.stats = await apiClient.get<AttendanceStats>('/api/attendance/my-stats');
        
        // Consultar rutina activa
        try {
          this.extraData = await apiClient.get('/api/routines/my-active');
        } catch {
          this.extraData = null;
        }
      } else if (this.user.role === 'ADMIN') {
        try {
          this.adminMetrics = await fetchAdminDashboardMetrics();
        } catch {
          this.adminMetrics = null;
        }
      } else {
        // COACH: cargar estadísticas de alumnos
        try {
          const students = await apiClient.get<any[]>('/api/users/students');
          this.extraData = {
            totalStudents: students.length,
          };
        } catch {
          this.extraData = { totalStudents: 0 };
        }
      }
    } catch (err) {
      console.warn('Error al cargar métricas de la API, se usarán valores por defecto:', err);
    }
  }

  private renderStudentDashboard(): string {
    const weekly = this.stats?.weeklyCount ?? 0;
    const monthly = this.stats?.monthlyCount ?? 0;
    const allTime = this.stats?.allTimeCount ?? 0;

    const routineName = this.extraData?.name || 'Ninguna';
    const routineDesc = this.extraData?.description || 'No posees una rutina activa asignada por un entrenador en este momento.';
    const routineExercises = this.extraData?.exercises?.length || 0;

    return `
      <div class="grid-stats">
        <div class="stat-card">
          <div class="stat-icon student-icon">
            ${getTyrisIcon('asistencia', { size: 22 })}
          </div>
          <div class="stat-info">
            <span class="stat-value">${weekly}</span>
            <span class="stat-label">Esta Semana</span>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon student-icon">
            ${getTyrisIcon('racha', { size: 22 })}
          </div>
          <div class="stat-info">
            <span class="stat-value">${monthly}</span>
            <span class="stat-label">Este Mes</span>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon honor-icon">
            ${getTyrisIcon('mis-records', { size: 22 })}
          </div>
          <div class="stat-info">
            <span class="stat-value">${allTime}</span>
            <span class="stat-label">Total Histórico</span>
          </div>
        </div>
      </div>

      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 1.5rem; margin-top: 1.5rem;">
        <!-- Card de Entrenamiento Rápido -->
        <div class="card-glass card-workout" style="display: flex; flex-direction: column; justify-content: space-between;">
          <div>
            <h3 style="font-size: 1.25rem; margin-bottom: 0.5rem; font-weight: 700;">¿Listo para Entrenar hoy?</h3>
            <p style="color: var(--text-muted); font-size: 0.9rem; margin-bottom: 1.5rem; line-height: 1.6;">
              Registra tu entrenamiento diario para seguir superando tus límites. La constancia es el pilar de la disciplina.
            </p>
          </div>
          <a href="#/workout" class="btn btn-student" style="padding: 0.85rem; text-align: center; justify-content: center; display: flex; align-items: center; gap: 0.5rem;" data-testid="btn-start-workout">
            ${getTyrisIcon('comenzar-entrenamiento', { size: 20 })}
            <span>Comenzar Entrenamiento</span>
          </a>
        </div>

        <!-- Card de Rutina Activa -->
        <div class="card-glass card-workout">
          <h3 style="font-size: 1.25rem; margin-bottom: 0.5rem; font-weight: 700;">Mi Rutina Activa</h3>
          <div style="background-color: rgba(255,255,255,0.03); border: 1px solid var(--border-metallic); border-radius: var(--radius-md); padding: 1.1rem; margin-bottom: 1.25rem;">
            <div style="font-weight: 700; font-size: 1.05rem; color: var(--tyris-energy); margin-bottom: 0.25rem;">
              ${routineName}
            </div>
            <p style="color: var(--text-muted); font-size: 0.85rem; line-height: 1.5;">
              ${routineDesc}
            </p>
          </div>
          ${routineExercises > 0 ? `
            <a href="#/daily-routine" class="btn btn-student" style="width: 100%; text-align: center; justify-content: center;">
              ${getTyrisIcon('rutina-del-dia', { size: 18 })}
              <span>Ver Rutina Detallada</span>
            </a>
          ` : `
            <button class="btn btn-outline" style="width: 100%; opacity: 0.5; justify-content: center;" disabled>
              Sin Rutina Activa
            </button>
          `}
        </div>
      </div>
    `;
  }

  private renderAdminDashboard(): string {
    const m = this.adminMetrics;
    if (!m) {
      return `
        <div class="card-glass admin-state-error" data-testid="admin-dashboard-error">
          <p>No se pudieron cargar las métricas del panel. Comprueba que el backend esté en ejecución.</p>
        </div>
      `;
    }

    return `
      <div data-testid="admin-dashboard">
        <div class="grid-stats">
          <a href="#/admin/students" class="stat-card stat-card-link" data-testid="kpi-active-students">
            <div class="stat-icon student-icon">
              ${getTyrisIcon('socios', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value">${m.activeStudents}</span>
              <span class="stat-label">Alumnos activos (pago al día)</span>
            </div>
          </a>

          <a href="#/admin/payments?tab=overdue" class="stat-card stat-card-link" data-testid="kpi-overdue">
            <div class="stat-icon" style="color: var(--color-error); background-color: rgba(229, 72, 77, 0.15);">
              ${getTyrisIcon('alerta', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value" style="color: ${m.overdueStudents > 0 ? 'var(--color-error)' : 'inherit'};">${m.overdueStudents}</span>
              <span class="stat-label">Cuota vencida</span>
            </div>
          </a>

          <a href="#/admin/payments?tab=upcoming" class="stat-card stat-card-link" data-testid="kpi-upcoming">
            <div class="stat-icon">
              ${getTyrisIcon('pagos', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value">${m.upcomingDue}</span>
              <span class="stat-label">Próximos vencimientos (7 días)</span>
            </div>
          </a>

          <a href="#/admin/routines" class="stat-card stat-card-link" data-testid="kpi-no-routine">
            <div class="stat-icon">
              ${getTyrisIcon('rutina-del-dia', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value">${m.withoutActiveRoutine}</span>
              <span class="stat-label">Sin rutina activa</span>
            </div>
          </a>

          <div class="stat-card" data-testid="kpi-machines">
            <div class="stat-icon" style="color: var(--color-error); background-color: rgba(229, 72, 77, 0.15);">
              ${getTyrisIcon('máquinas', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value">${m.brokenMachines}</span>
              <span class="stat-label">Máquinas rotas o con fallas</span>
            </div>
          </div>
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem; margin-top: 1.5rem;">
          <div class="card-glass card-management">
            <h3 class="admin-section-title" style="font-size:1.1rem; font-weight:700; margin-bottom:1rem;">Morosos recientes</h3>
            ${
              m.overdueList.length === 0
                ? '<p class="admin-cell-muted">No hay alumnos con cuota vencida.</p>'
                : `
              <ul class="admin-mini-list">
                ${m.overdueList
                  .map(
                    (s) => `
                  <li>
                    <a href="#/admin/student?id=${s.id}">${s.firstName} ${s.lastName}</a>
                    <span class="admin-mini-list-muted">${s.email}</span>
                  </li>
                `,
                  )
                  .join('')}
              </ul>
              <a href="#/admin/payments?tab=overdue" class="btn btn-outline btn-sm" style="margin-top:1rem;">Ver todos →</a>
            `
            }
          </div>

          <div class="card-glass card-management">
            <h3 class="admin-section-title" style="font-size:1.1rem; font-weight:700; margin-bottom:1rem;">Próximos vencimientos</h3>
            ${
              m.upcomingList.length === 0
                ? '<p class="admin-cell-muted">Nada por vencer en los próximos 7 días.</p>'
                : `
              <ul class="admin-mini-list">
                ${m.upcomingList
                  .map(
                    (s) => `
                  <li>
                    <a href="#/admin/student?id=${s.id}">${s.firstName} ${s.lastName}</a>
                    <span class="badge badge-honor">${s.daysUntilDue} días · ${formatDate(s.nextDueDate)}</span>
                  </li>
                `,
                  )
                  .join('')}
              </ul>
              <a href="#/admin/payments?tab=upcoming" class="btn btn-outline btn-sm" style="margin-top:1rem;">Ver todos →</a>
            `
            }
          </div>

          <div class="card-glass card-management">
            <h3 class="admin-section-title" style="font-size:1.1rem; font-weight:700; margin-bottom:1rem;">Accesos rápidos</h3>
            <div style="display:flex;flex-direction:column;gap:0.6rem;">
              <a href="#/admin/students" class="btn btn-primary" data-testid="admin-link-students">
                ${getTyrisIcon('socios', { size: 18 })}
                <span>Gestionar usuarios & socios</span>
              </a>
              <a href="#/admin/payments" class="btn btn-outline" data-testid="admin-link-payments">
                ${getTyrisIcon('pagos', { size: 18 })}
                <span>Pagos y membresías</span>
              </a>
              <a href="#/admin/exercises" class="btn btn-outline" data-testid="admin-link-exercises">
                ${getTyrisIcon('mis-rutinas', { size: 18 })}
                <span>Catálogo ejercicios</span>
              </a>
              <a href="#/admin/machines" class="btn btn-outline" data-testid="admin-link-machines">
                ${getTyrisIcon('máquinas', { size: 18 })}
                <span>Parque máquinas</span>
              </a>
              <a href="#/admin/routines" class="btn btn-outline" data-testid="admin-link-routines">
                ${getTyrisIcon('rutina-del-dia', { size: 18 })}
                <span>Rutinas asignadas</span>
              </a>
              <a href="#/admin/routine-templates" class="btn btn-outline" data-testid="admin-link-templates">
                ${getTyrisIcon('mis-rutinas', { size: 18 })}
                <span>Templates de rutina</span>
              </a>
              <a href="#/admin/measures" class="btn btn-outline">
                ${getTyrisIcon('medidas', { size: 18 })}
                <span>Medidas de alumnos</span>
              </a>
            </div>
            <p class="admin-cell-muted" style="margin-top:1rem;font-size:0.8rem; color:var(--text-muted);">
              ${m.totalStudents} alumnos registrados en el sistema.
            </p>
          </div>
        </div>
      </div>
    `;
  }

  private renderStaffDashboard(): string {
    const totalStudents = this.extraData?.totalStudents ?? 0;
    return `
      <div data-testid="coach-dashboard">
        <div class="grid-stats">
          <a href="#/admin/students" class="stat-card stat-card-link" data-testid="coach-link-students">
            <div class="stat-icon">
              ${getTyrisIcon('socios', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value">${totalStudents}</span>
              <span class="stat-label">Total Alumnos</span>
            </div>
          </a>
          <a href="#/admin/routines" class="stat-card stat-card-link" data-testid="coach-link-routines">
            <div class="stat-icon">
              ${getTyrisIcon('rutina-del-dia', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value" style="font-size:1.5rem;">→</span>
              <span class="stat-label">Rutinas Asignadas</span>
            </div>
          </a>
          <a href="#/admin/routine-templates" class="stat-card stat-card-link" data-testid="coach-link-templates">
            <div class="stat-icon">
              ${getTyrisIcon('mis-rutinas', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value" style="font-size:1.5rem;">→</span>
              <span class="stat-label">Templates</span>
            </div>
          </a>
          <a href="#/admin/measures" class="stat-card stat-card-link" data-testid="coach-link-measures">
            <div class="stat-icon">
              ${getTyrisIcon('medidas', { size: 22 })}
            </div>
            <div class="stat-info">
              <span class="stat-value" style="font-size:1.5rem;">→</span>
              <span class="stat-label">Medidas</span>
            </div>
          </a>
        </div>

        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:1.5rem;margin-top:1.5rem;">
          <div class="card-glass card-management">
            <h3 class="admin-section-title" style="font-size:1.1rem; font-weight:700; margin-bottom:1rem;">Accesos rápidos</h3>
            <div style="display:flex;flex-direction:column;gap:0.6rem;">
              <a href="#/admin/students" class="btn btn-primary" data-testid="coach-btn-students">
                ${getTyrisIcon('socios', { size: 18 })}
                <span>Ver alumnos</span>
              </a>
              <a href="#/admin/routines" class="btn btn-outline" data-testid="coach-btn-routines">
                ${getTyrisIcon('rutina-del-dia', { size: 18 })}
                <span>Rutinas asignadas</span>
              </a>
              <a href="#/admin/routine-templates" class="btn btn-outline" data-testid="coach-btn-templates">
                ${getTyrisIcon('mis-rutinas', { size: 18 })}
                <span>Templates de rutina</span>
              </a>
              <a href="#/admin/measures" class="btn btn-outline" data-testid="coach-btn-measures">
                ${getTyrisIcon('medidas', { size: 18 })}
                <span>Medidas corporales</span>
              </a>
              <a href="#/admin/attendance" class="btn btn-outline" data-testid="coach-btn-attendance">
                ${getTyrisIcon('asistencia', { size: 18 })}
                <span>Asistencia</span>
              </a>
              <a href="#/admin/rankings" class="btn btn-outline" data-testid="coach-btn-rankings">
                ${getTyrisIcon('rankings', { size: 18 })}
                <span>Rankings</span>
              </a>
              <a href="#/admin/exercises" class="btn btn-outline" data-testid="coach-btn-exercises">
                ${getTyrisIcon('mis-rutinas', { size: 18 })}
                <span>Catálogo ejercicios</span>
              </a>
            </div>
          </div>

          <div class="card-glass card-management">
            <h3 class="admin-section-title" style="font-size:1.1rem; font-weight:700; margin-bottom:1rem;">Tu rol como Entrenador</h3>
            <p style="color:var(--text-muted);font-size:0.9rem;line-height:1.7;margin-bottom:1rem;">
              Como <strong style="color:var(--tyris-iris);">COACH</strong> puedes:
            </p>
            <ul style="color:var(--text-muted);font-size:0.88rem;line-height:2;padding-left:1.2rem;">
              <li>Ver y gestionar fichas de alumnos</li>
              <li>Asignar y editar rutinas personalizadas</li>
              <li>Crear y aplicar templates de rutina</li>
              <li>Registrar y editar medidas corporales</li>
              <li>Consultar asistencia y rankings</li>
              <li>Gestionar el catálogo de ejercicios</li>
            </ul>
            <p style="color:var(--text-muted);font-size:0.8rem;margin-top:1rem;padding-top:1rem;border-top:1px solid var(--border-metallic);display:inline-flex;align-items:center;gap:0.3rem;">
              ${getTyrisIcon('darse-de-baja', { size: 14 })} Pagos y máquinas son exclusivos del rol ADMIN.
            </p>
          </div>
        </div>
      </div>
    `;
  }
}
